import React from "react";
import {
  ArrowUpRight,
  BotOff,
  CheckCircle2,
  Code2,
  Github,
  Globe2,
  Layers3,
} from "lucide-react";
import portfolio from "../data/portfolio.json";

const { projects } = portfolio;

const Projects = () => {
  return (
    <section id="projects" className="relative py-32 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px circle at 15% 10%, rgba(94,234,212,0.06), transparent 48%), radial-gradient(700px circle at 85% 45%, rgba(251,191,36,0.04), transparent 50%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="flex items-center gap-4 mb-14">
          <span className="font-mono text-xs text-teal-300">03 / Projects</span>
          <div className="h-px flex-1 bg-gradient-to-r from-zinc-800 to-transparent" />
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <h2
              className="font-display font-bold text-white tracking-tight"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.25rem)" }}
            >
              A couple of projects{" "}
              <span className="text-teal-300">worth opening</span>.
            </h2>
          </div>

          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-zinc-400 leading-relaxed max-w-2xl">
              These are older projects that I brought back online as portfolio
              demos. They are not production projects anymore, but the code and
              the main flows are still there.
            </p>
          </div>
        </div>

        <div className="grid gap-6">
          {projects.map((project) => (
            <article
              key={project.id}
              className="group relative overflow-hidden rounded-lg border border-zinc-800/80 bg-zinc-950/60 p-6 md:p-8 transition-colors hover:border-teal-300/35"
            >
              <div className="absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100">
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(650px circle at 12% 0%, rgba(94,234,212,0.08), transparent 45%)",
                  }}
                />
              </div>

              <div className="relative grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                  <div className="flex flex-wrap items-center gap-3 mb-5">
                    <span className="inline-flex items-center gap-2 rounded-md border border-teal-300/25 bg-teal-300/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-teal-200">
                      <Layers3 className="w-3.5 h-3.5" />
                      {project.category}
                    </span>
                    <span className="rounded-md border border-zinc-700/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                      {project.status}
                    </span>
                  </div>

                  <h3 className="font-display text-3xl md:text-4xl font-bold text-white">
                    {project.title}
                  </h3>

                  <div className="mt-6 flex items-center gap-3 text-sm text-zinc-300">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900/70 text-teal-300">
                      <BotOff className="w-4 h-4" />
                    </span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        AI used
                      </div>
                      <div className="text-zinc-200">
                        {project.aiUsed ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-8">
                  <p className="text-zinc-300 leading-relaxed">
                    {project.purpose}
                  </p>

                  <div className="mt-7 grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        <CheckCircle2 className="w-4 h-4 text-teal-300" />
                        Scope
                      </div>
                      <ul className="space-y-2.5">
                        {project.highlights.map((highlight) => (
                          <li key={highlight} className="flex gap-3 text-sm leading-relaxed text-zinc-400">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-300/80" />
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        <Code2 className="w-4 h-4 text-amber-300" />
                        Technologies
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech) => (
                          <span
                            key={tech}
                            className="rounded-md border border-zinc-800 bg-zinc-900/70 px-2.5 py-1.5 text-xs text-zinc-300"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <a
                      href={project.repoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-10 items-center gap-2 rounded-md border border-zinc-700 px-4 text-sm text-zinc-200 transition-colors hover:border-teal-300/50 hover:bg-white/5 hover:text-white"
                    >
                      <Github className="w-4 h-4" />
                      Repo
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>

                    {project.siteLinks.map((site) => (
                      <a
                        key={site.url}
                        href={site.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-10 items-center gap-2 rounded-md bg-teal-300 px-4 text-sm font-medium text-zinc-950 transition-colors hover:bg-teal-200"
                      >
                        <Globe2 className="w-4 h-4" />
                        {site.label}
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
